/**
 * useFixedCostSources Hook
 *
 * Manages fixed cost source mappings (Xero contact → cost category)
 * and triggers sync from Xero.
 */

import { useState, useEffect, useCallback } from 'react'
import { supabase } from '@/supabase-client'

const FUNCTION_NAME = 'xero-cost-sync'

async function callFunction(path, options = {}) {
  const { method = 'GET', body, params } = options

  let fullPath = `${FUNCTION_NAME}/${path}`
  if (params) {
    fullPath += `?${params.toString()}`
  }

  const invokeOptions = { method }
  if (body) {
    invokeOptions.body = body
  }

  const { data, error } = await supabase.functions.invoke(fullPath, invokeOptions)

  if (error) {
    throw new Error(error.message || 'Edge function error')
  }

  if (data?.error) {
    const err = new Error(data.message || data.error)
    err.retryable = data.retryable ?? false
    throw err
  }

  return data
}

export function useFixedCostSources() {
  const [sources, setSources] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [isSyncing, setIsSyncing] = useState(false)
  const [error, setError] = useState(null)

  const fetchSources = useCallback(async () => {
    setIsLoading(true)
    setError(null)
    try {
      const response = await callFunction('sources')
      setSources(response.data || [])
    } catch (err) {
      console.error('[useFixedCostSources] Failed to fetch sources:', err)
      setError(err.message)
      setSources([])
    } finally {
      setIsLoading(false)
    }
  }, [])

  const addSource = useCallback(async (sourceData) => {
    const response = await callFunction('sources', {
      method: 'POST',
      body: sourceData
    })
    await fetchSources()
    return response.data
  }, [fetchSources])

  const updateSource = useCallback(async (id, sourceData) => {
    const response = await callFunction(`sources/${id}`, {
      method: 'PUT',
      body: sourceData
    })
    await fetchSources()
    return response.data
  }, [fetchSources])

  const deleteSource = useCallback(async (id) => {
    await callFunction(`sources/${id}`, { method: 'DELETE' })
    await fetchSources()
  }, [fetchSources])

  const syncCosts = useCallback(async (monthsBack = 12) => {
    setIsSyncing(true)
    setError(null)
    try {
      const response = await callFunction('sync', {
        method: 'POST',
        body: { months_back: monthsBack }
      })
      return response.data
    } catch (err) {
      console.error('[useFixedCostSources] Sync failed:', err)
      setError(err.message)
      throw err
    } finally {
      setIsSyncing(false)
    }
  }, [])

  useEffect(() => {
    fetchSources()
  }, [fetchSources])

  return {
    sources,
    isLoading,
    isSyncing,
    error,
    addSource,
    updateSource,
    deleteSource,
    syncCosts,
    refresh: fetchSources
  }
}

export default useFixedCostSources
